"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { AlertCircle, Phone, Search, X } from "lucide-react";
import { geoService, type Prediction } from "../../services/geoService";
import { Agency, useGeo } from "@/app/context/GeoContext";
import { resolveLegacyLink } from "@/app/utils/linkUtils";

type AddressInputProps = {
    redirectOnSelect?: boolean;
    showAgencyResult?: boolean;

    /*
     * true : enregistre l'agence dans le GeoContext / localStorage.
     * false : n'enregistre pas l'agence, utile dans PreCommandePopup.
     */
    persistAgency?: boolean;
};

export default function AddressInput({ redirectOnSelect = true, showAgencyResult = false, persistAgency = true, }: AddressInputProps) {
    const [query, setQuery] = useState("");
    const [predictions, setPredictions] = useState<Prediction[]>([]);
    const [showPredictions, setShowPredictions] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Loading après le choix d'une adresse
    const [loading, setLoading] = useState(false);

    // Animation pendant la redirection
    const [isRedirecting, setIsRedirecting] = useState(false);

    const [nearestAgency, setNearestAgency] = useState<Agency | null>(null);
    const [isFallbackAgency, setIsFallbackAgency] = useState(false);

    const isSelectingRef = useRef(false);

    const { setAgency, setHasSelected, clearAgency } = useGeo();
    const router = useRouter();

    useEffect(() => {
        const handler = setTimeout(async () => {
            if (isSelectingRef.current) {
                isSelectingRef.current = false;
                return;
            }

            if (query.length >= 3) {
                try {
                    const data = await geoService.getPredictions(query);

                    const addresses = data.filter((d: Prediction) =>
                        d.types.some((t) =>
                            ["subpremise", "street_address", "geocode"].includes(t)
                        )
                    );

                    setPredictions(addresses);
                    setShowPredictions(true);
                } catch (err) {
                    console.error("Erreur API predictions:", err);
                }
            } else {
                setPredictions([]);
                setShowPredictions(false);
            }
        }, 400);

        return () => clearTimeout(handler);
    }, [query]);

    const handleSelectPlace = async (placeId: string, description: string) => {
        isSelectingRef.current = true;

        setQuery(description);
        setPredictions([]);
        setShowPredictions(false);
        setError(null);

        // Ici commence l'effet de patience
        setLoading(true);
        setIsRedirecting(false);

        setNearestAgency(null);
        setIsFallbackAgency(false);

        try {
            const agency = await geoService.getMap(placeId);

            const noAgencyMessage =
                typeof agency === "string" ? agency : agency?.message;

            /*
             * Cas 1 : agence exacte trouvée
             */
            if (agency && typeof agency !== "string" && agency.id) {
                const selectedAgency: Agency = {
                    ...agency,
                    phoneIdelio: agency.phoneIdelio || "",
                };

                if (persistAgency) {
                    clearAgency();
                    setAgency(selectedAgency);
                    setHasSelected(true);
                }

                setNearestAgency(selectedAgency);
                setIsFallbackAgency(false);

                if (redirectOnSelect) {
                    if (agency.url_agence) {
                        setIsRedirecting(true);

                        router.push(
                            resolveLegacyLink(`/portage-de-repas/${agency.url_agence}`)
                        );
                    } else {
                        setError("Agence introuvable pour cette adresse.");
                    }
                }

                return;
            }

            /*
             * Cas 2 : aucune agence exacte trouvée
             * On cherche l’agence la plus proche.
             */
            if (persistAgency) {
                clearAgency();
                setHasSelected(false);
            }

            try {
                const nearest = await geoService.getNearestAgency(placeId);

                if (nearest && nearest.id) {
                    const fallbackAgency: Agency = {
                        ...nearest,
                        phoneIdelio: nearest.phoneIdelio || "",
                    };

                    setNearestAgency(fallbackAgency);
                    setIsFallbackAgency(true);
                    setError(null);

                    if (persistAgency) {
                        setAgency(fallbackAgency);
                        setHasSelected(true);
                    }

                    if (redirectOnSelect) {
                        if (nearest.url_agence) {
                            setIsRedirecting(true);

                            router.push(
                                resolveLegacyLink(`/portage-de-repas/${nearest.url_agence}`)
                            );
                        } else {
                            setError("Agence la plus proche introuvable.");
                        }
                    }

                    return;
                }

                setNearestAgency(null);
                setIsFallbackAgency(false);
                setError(
                    noAgencyMessage ||
                    "Aucune agence proche n’a été trouvée pour cette adresse."
                );
            } catch (fallbackError) {
                console.error("Erreur nearestAgency:", fallbackError);
                setNearestAgency(null);
                setIsFallbackAgency(false);
                setError("Impossible de trouver l’agence la plus proche.");
            }
        } catch (err) {
            console.error("Erreur getMap:", err);
            setNearestAgency(null);
            setIsFallbackAgency(false);
            setError("Impossible de vérifier l'adresse. Réessayez.");
        } finally {
            /*
             * Si redirectOnSelect=true et qu'on a lancé router.push,
             * on garde l'effet jusqu'au changement de page.
             */
            if (!redirectOnSelect || !isRedirecting) {
                setLoading(false);
            }
        }
    };

    const handleReset = () => {
        setQuery("");
        setPredictions([]);
        setShowPredictions(false);
        setError(null);
        setLoading(false);
        setIsRedirecting(false);

        setNearestAgency(null);
        setIsFallbackAgency(false);

        if (persistAgency) {
            clearAgency();
            setHasSelected(false);
        }
    };

    const agencyPhone = nearestAgency?.phone || "";


    const showSnakeBorder = loading || isRedirecting;

    return (
        <div className="relative w-full">
            {/* Champ de saisie */}
            <div
                className={`relative overflow-hidden rounded-lg p-[2px] transition-all duration-400 ${showSnakeBorder
                        ? `
              before:absolute before:inset-[-120%] before:content-['']
              before:animate-spin
              before:bg-[conic-gradient(from_0deg,transparent_0deg,transparent_250deg,#699F11_285deg,#EDFBCC_320deg,transparent_350deg)]
            `
                        : ""
                    }`}
            >
                <Search className="absolute left-4 top-1/2 z-20 h-5 w-5 -translate-y-1/2 text-gray-400" />

                <input
                    type="text"
                    id="address-input"
                    placeholder="Saisir l'adresse de livraison"
                    value={query}
                    readOnly={showSnakeBorder}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setNearestAgency(null);
                        setIsFallbackAgency(false);
                        setError(null);
                        setIsRedirecting(false);
                    }}
                    onFocus={() => {
                        if (predictions.length > 0) {
                            setShowPredictions(true);
                        }
                    }}
                    onBlur={() => {
                        setTimeout(() => {
                            setShowPredictions(false);
                        }, 300);
                    }}
                    className={`relative z-10 w-full rounded-[7px] bg-white pl-10 pr-10 py-2 outline-none transition-all duration-200 focus-visible:ring-0 ${showSnakeBorder
                            ? "border border-transparent"
                            : error
                                ? "border border-secondary"
                                : "border border-primaryRed hover:border-3 hover:border-primaryGreen hover:shadow-[0_15px_20px_0_rgba(0,0,0,0.15)] focus-visible:border-2 focus-visible:border-primaryGreen"
                        }`}
                />

                {query && !showSnakeBorder && (
                    <button
                        type="button"
                        onClick={handleReset}
                        className="absolute right-3 top-1/2 z-20 -translate-y-1/2 text-gray-400 hover:text-black"
                    >
                        <X className="h-5 w-5" />
                    </button>
                )}
            </div>

            {/* Liste des suggestions */}
            {showPredictions && predictions.length > 0 && (
                <ul
                    className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-lg border bg-white shadow-lg"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {predictions.map((p) => (
                        <li
                            key={p.placeId}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowPredictions(false);
                                handleSelectPlace(p.placeId, p.description);
                            }}
                            role="button"
                            tabIndex={0}
                            className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
                        >
                            {p.description}
                        </li>
                    ))}
                </ul>
            )}

            {/* Loader */}
            {loading && (
                <p className="mt-2 text-sm text-primaryGreen">
                    Recherche de l’agence la plus proche...
                </p>
            )}

            {/* Message d’erreur */}
            {error && (
                <div className="z-40 mt-2 flex items-center gap-2 rounded border border-[#FDA29B] bg-[#FEF3F2] px-3 py-2 text-sm text-text">
                    <AlertCircle className="h-5 w-5 shrink-0 text-secondary" />
                    <span>{error}</span>
                </div>
            )}

            {/* Résultat agence : affiché seulement si demandé */}
            {showAgencyResult && nearestAgency && (
                <>
                    <div className="mt-4 rounded-lg border border-primaryGreen/30 bg-[#F5FFE9] px-4 py-3 text-center">
                        <p className="text-sm font-semibold text-primaryGreen">
                            {isFallbackAgency
                                ? "Agence la plus proche disponible"
                                : "Agence disponible"}
                        </p>

                        <p className="mt-1 text-base font-bold text-text">
                            {nearestAgency.lmsName || nearestAgency.name}
                        </p>

                        {nearestAgency.adresse && (
                            <p className="mt-1 text-sm text-gray-600">
                                {nearestAgency.adresse}
                            </p>
                        )}
                    </div>

                    {agencyPhone && (
                        <div className="mt-6 flex flex-col gap-4 rounded-lg border border-gray-200 bg-[#F7F9FF] px-5 py-2 lg:py-5 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-start text-sm font-semibold leading-relaxed text-text sm:flex-1 sm:pr-6">
                                Contactez votre agence locale pour échanger sur vos besoins et
                                vérifier la faisabilité de votre demande.
                            </p>

                            <a
                                href={`tel:${String(agencyPhone).replace(/\s/g, "")}`}
                                className="flex shrink-0 items-center justify-center gap-3 rounded-md bg-secondary px-6 py-4 text-white transition hover:bg-primaryGreen"
                            >
                                <Phone size={20} className="shrink-0" />

                                <span className="text-center leading-tight">
                                    <strong className="block whitespace-nowrap text-xl font-bold">
                                        {agencyPhone}
                                    </strong>

                                    <span className="block text-sm font-semibold">
                                        Appeler l’agence
                                    </span>
                                </span>
                            </a>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}